import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGates, updateGate, patchGate, createGate, getGateDuplicateAnalysis,
  mergeGates, cleanupOrphanGates, mergeIdenticalGates, mergeRoutes,
} from '../api/gates';
import { Gate } from '../types/Gate';

export const useGates = (queryString: string = '') =>
  useQuery({
    queryKey: ['gates', queryString],
    queryFn: () => getGates(queryString),
  });

export const useGatesWMinRoute = () =>
  useQuery({
    queryKey: ['gates', 'minimal_route'],
    queryFn: () => getGates('scope=minimal_route'),
  });

export const useUpdateGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gateId, data }: { gateId: string; data: Partial<Gate> }) =>
      updateGate(gateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
      // Route Management and the route picker read gates nested inside
      // routes (`routes?scope=route_management`), not from the gates queries.
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      queryClient.invalidateQueries({ queryKey: ['runAndCalibration'] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
};

/** Pin or unpin a gate pair on the team-wide shortlist. */
export const useSetGatePinned = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gateId, pinned }: { gateId: string; pinned: boolean }) =>
      patchGate(gateId, { pinned }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
      queryClient.invalidateQueries({ queryKey: ['routes'] });
    },
  });
};

export const useCreateGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Gate>) => createGate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
    },
  });
};

export const useGateDuplicateAnalysis = (enabled: boolean = false) =>
  useQuery({
    queryKey: ['gates', 'duplicate_analysis'],
    queryFn: getGateDuplicateAnalysis,
    enabled,
  });

export const useMergeGates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (gateIds: string[]) => mergeGates(gateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
};

export const useMergeIdenticalGates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (routeId: string) => mergeIdenticalGates(routeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
};

export const useMergeRoutes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (routeIds: string[]) => mergeRoutes(routeIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      queryClient.invalidateQueries({ queryKey: ['gates'] });
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
};

export const useCleanupOrphanGates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => cleanupOrphanGates(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gates'] });
    },
  });
};
